/* Adding to the cart without losing the page.
 *
 * The form posts to /cart/add and, left alone, Shopify answers with the cart
 * page. On a catalogue sold in multipacks and bundles that ends the shopping
 * trip at the till every single time: to buy a second thing you have to go
 * back. So the add happens here instead, and the page stays where it was.
 *
 * What confirms it is the design system's own sparkle - ksg-motion.css has
 * carried `ksg-sparkle-once` and the comment "add-to-bag success" since the
 * beginning, with nothing firing it. The cart mark makes the movement once,
 * the count goes up, and a line of text says so.
 *
 * Three things this is careful about:
 *
 *   Buy now still leaves. It carries return_to and means it.
 *
 *   Without this file the form posts as it always did and the shopper lands
 *   on the cart page. Nothing here is required for the shop to work; it is
 *   required for the shop to be pleasant.
 *
 *   The sparkle is decoration, not the message. Under prefers-reduced-motion
 *   every animation in this theme is switched off, so the confirmation has
 *   to survive without it - which is why the count, the label and the line
 *   of text all change too.
 */

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const toast = document.querySelector('[data-ksg-added]');
const toastText = document.querySelector('[data-ksg-added-text]');
const restText = toastText ? toastText.textContent : '';
let hideTimer;

const announce = (message, ok) => {
  if (!toast || !toastText) return;
  toastText.textContent = message;
  toast.classList.toggle('ksg-added--error', !ok);
  toast.hidden = false;
  clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    toast.hidden = true;
    toastText.textContent = restText;
    toast.classList.remove('ksg-added--error');
  }, 4000);
};

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!/\/cart\/add(?:$|\?)/.test(form.getAttribute('action') || '')) return;

  /* Buy now is a submit button with a name - return_to=/checkout. It is
     supposed to leave the page, so it is left alone. */
  const button = event.submitter;
  if (button && button.name) return;

  event.preventDefault();

  const label = button ? button.textContent : '';
  const adding = button && button.dataset.labelAdding;
  const added = button && button.dataset.labelAdded;

  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (adding) button.textContent = adding;
  }

  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    });

    if (!res.ok) {
      /* Shopify puts the reason in `description` - out of stock, or more
         than the inventory allows. It is already localised, so it is shown
         as sent rather than replaced with something vaguer. */
      const problem = await res.json().catch(() => null);
      throw new Error((problem && (problem.description || problem.message)) || '');
    }

    /* The badge, and the sparkle that goes with it, belong to
       ksg-cart-count.js - the one module on every page. This says the cart
       changed; keeping the header honest about it is not this file's job. */
    document.dispatchEvent(new CustomEvent('ksg:cart:changed'));
    if (button && added) button.textContent = added;
    announce(restText, true);
  } catch (error) {
    const message = error && error.message;
    announce(message || (toast ? toast.dataset.errorFallback || restText : ''), false);
    if (button) button.textContent = label;
  } finally {
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      /* The success label is a moment, not a new name for the button. */
      setTimeout(() => {
        if (button.textContent !== label) button.textContent = label;
      }, 1600);
    }
  }
});